// src/hr/portal/PersonProfile.js -- one place for everything about one person (mockup screen 3):
// a header with their record facts, then tabs -- Time off, Record, Approvals, Team, Policies,
// Checklists, Reminders, Change log. The tab lives in the URL (?tab=record) so it survives a reload and
// can be linked to. Notes on entries are HR-only and appear here, never in lists elsewhere.
import React, { useCallback, useEffect, useId, useMemo, useRef, useState } from 'react';
import { Link, useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { FiAlertCircle, FiAlertTriangle, FiArrowLeft, FiLock, FiUsers, FiUserX } from 'react-icons/fi';
import { hr } from '../api';
import { Avatar, Btn, Card, EmptyState, Pill, Skeleton } from '../kit';
import { plainId } from '../kit/util';
import { shortDate } from '../timeoff/dates';
import LogTimeOffDrawer from './LogTimeOffDrawer';
import ProfileTimeOff from './ProfileTimeOff';
import ProfileRecord from './ProfileRecord';
import ProfileApprovals from './ProfileApprovals';
import { ProfileChangeLog, ProfileChecklists, ProfilePolicies, ProfileReminders } from './ProfileLists';
import {
  accountPill, anniversaryLabel, employmentLabel, fullDate, fullName, lifecyclePill, tenureLabel
} from './portalUtils';

const TABS = [
  { key: 'time-off', label: 'Time off' },
  { key: 'record', label: 'Record' },
  { key: 'approvals', label: 'Approvals' },
  { key: 'team', label: 'Team' },
  { key: 'policies', label: 'Policies' },
  { key: 'checklists', label: 'Checklists' },
  { key: 'reminders', label: 'Reminders' },
  { key: 'changelog', label: 'Change log' }
];

// GET /admin/people/:id. reload() keeps the profile on screen until the new copy arrives; patch()
// applies a change the server already confirmed.
function useProfile(id) {
  const [state, setState] = useState({ id, status: 'loading', data: null });
  const latest = useRef(0);

  const load = useCallback(async (keep) => {
    latest.current += 1;
    const call = latest.current;
    setState((s) => (keep && s.id === id ? { ...s, status: 'loading' } : { id, status: 'loading', data: null }));
    try {
      const data = await hr.admin.people.get(id);
      if (call === latest.current) setState({ id, status: 'ready', data: data || null });
    } catch (err) {
      const missing = err && err.response && err.response.status === 404;
      if (call === latest.current) setState((s) => ({ ...s, id, status: missing ? 'missing' : 'error' }));
    }
  }, [id]);

  useEffect(() => {
    load(false);
    return () => { latest.current += 1; };
  }, [load]);

  const patch = useCallback((fn) => setState((s) => (s.data ? { ...s, data: fn(s.data) } : s)), []);
  const reload = useCallback(() => load(true), [load]);
  return { ...state, reload, patch };
}

function Facts({ person, today }) {
  const facts = [
    { label: 'Hire date', value: person.hireDate ? fullDate(person.hireDate) : 'Not set', sub: tenureLabel(person.hireDate, today) },
    { label: 'Manager', value: person.managerName || 'Not set' }
  ];
  if (person.lastDay && person.status !== 'active') {
    facts.push({ label: 'Last day', value: fullDate(person.lastDay), sub: person.retainUntil ? `Kept until ${fullDate(person.retainUntil)}` : undefined });
  } else {
    facts.push({ label: 'Anniversary', value: person.hireDate ? shortDate(person.hireDate) : '—', sub: anniversaryLabel(person.hireDate, today) });
  }
  return (
    <dl className="hrp-pf-facts">
      {facts.map((f) => (
        <div key={f.label}>
          <dt>{f.label}</dt>
          <dd>
            <b>{f.value}</b>
            {f.sub && <em>{f.sub}</em>}
          </dd>
        </div>
      ))}
    </dl>
  );
}

function Header({ data, today }) {
  const p = data.person;
  const name = fullName(p);
  const account = accountPill(p.accountStatus, { long: true });
  const life = lifecyclePill(p) || { tone: 'ok', label: 'Active' };
  return (
    <Card feat delay={0} className="hrp-pf-head">
      <Avatar id={p.id} name={name} size="lg" />
      <div className="hrp-pf-id">
        <h1 className="hrp-pf-name">{name}</h1>
        <p className="hrp-pf-meta">{[p.title, p.department, p.email].filter(Boolean).join(' · ')}</p>
        <div className="hrp-pf-pills">
          <Pill tone={account.tone} dot>{account.label}</Pill>
          <Pill tone="mute">{employmentLabel(p.employmentType)}</Pill>
          <Pill tone={life.tone}>{life.label}</Pill>
          {p.legalHold && <Pill tone="warn" icon={<FiLock />}>Legal hold</Pill>}
          {(data.sickRuns || []).length > 0 && <Pill tone="bad" icon={<FiAlertTriangle />}>3+ sick days in a row</Pill>}
        </div>
      </div>
      <Facts person={p} today={today} />
    </Card>
  );
}

// The Team tab: the person's direct reports (active people who have them as their manager), each
// linked to their own profile. Name, title and department only.
function ProfileTeam({ data }) {
  const team = data.team || [];
  const name = fullName(data.person);
  const count = `${team.length} direct ${team.length === 1 ? 'report' : 'reports'}`;
  return (
    <Card delay={1} as="section" aria-label={`Team · ${name}`} title="Direct reports" className="hrp-pfl hrp-pf-team"
      action={team.length > 0 ? <span className="hrp-pf-team__n">{count}</span> : null}
    >
      {team.length === 0 ? (
        <EmptyState compact icon={FiUsers} title="No direct reports">
          {`People who have ${data.person.firstName || name} as their manager show up here. Set someone's manager on their Record tab.`}
        </EmptyState>
      ) : (
        <ul className="hrp-pfl__list hrp-pf-team__list" aria-label={`Direct reports of ${name}`}>
          {team.map((p) => {
            const reportName = fullName(p);
            return (
              <li key={p.id} className="hrp-pfl__it">
                <Avatar id={p.id} name={reportName} size="md" />
                <div className="hrp-pfl__txt">
                  <Link to={`../people/${p.id}`} className="hrp-pf-team__link">{reportName}</Link>
                  <span>{[p.title, p.department].filter(Boolean).join(' · ') || 'No title or department yet'}</span>
                </div>
              </li>
            );
          })}
        </ul>
      )}
    </Card>
  );
}

function Tabs({ tabs, current, onChange, idBase }) {
  const refs = useRef({});
  const onKeyDown = (e, i) => {
    const moves = { ArrowRight: i + 1, ArrowLeft: i - 1, Home: 0, End: tabs.length - 1 };
    if (!(e.key in moves)) return;
    e.preventDefault();
    const next = tabs[(moves[e.key] + tabs.length) % tabs.length];
    onChange(next.key);
    if (refs.current[next.key]) refs.current[next.key].focus();
  };
  return (
    <div className="hrp-utabs hrp-pf-tabs" role="tablist" aria-label="Profile sections">
      {tabs.map((t, i) => (
        <button
          key={t.key}
          ref={(el) => { refs.current[t.key] = el; }}
          type="button"
          role="tab"
          id={`${idBase}-tab-${t.key}`}
          aria-selected={current === t.key}
          aria-controls={`${idBase}-panel`}
          tabIndex={current === t.key ? 0 : -1}
          className="hrp-utab"
          onClick={() => onChange(t.key)}
          onKeyDown={(e) => onKeyDown(e, i)}
        >
          {t.label}
          {t.badge > 0 && <span className="hrp-tab__n"><span className="hrp-sr">(</span>{t.badge}<span className="hrp-sr">{` ${t.badgeWord})`}</span></span>}
        </button>
      ))}
    </div>
  );
}

function LoadingProfile() {
  return (
    <div className="hrp-loading">
      <Skeleton label="Loading their profile" variant="card" />
      <Skeleton width={420} height={14} className="hrp-loading__sub" />
      <div className="hrp-pf-cols">
        <div className="hrp-stack">
          <Skeleton variant="block" height={190} />
          <Skeleton variant="block" height={220} />
        </div>
        <div className="hrp-stack">
          <Skeleton variant="block" height={150} />
          <Skeleton variant="block" height={170} />
        </div>
      </div>
    </div>
  );
}

export function PersonProfile({ me, today, people, types, holidays, context, yearRange, onToast }) {
  const { id } = useParams();
  const navigate = useNavigate();
  const [params, setParams] = useSearchParams();
  const profile = useProfile(id);
  const [log, setLog] = useState(null); // { entry } while the drawer is open
  const idBase = `hrp-pf-${plainId(useId())}`;
  const panelRef = useRef(null);

  const tab = TABS.some((t) => t.key === params.get('tab')) ? params.get('tab') : 'time-off';
  const setTab = useCallback((key) => {
    setParams(key === 'time-off' ? {} : { tab: key }, { replace: true });
  }, [setParams]);
  const goTab = useCallback((key) => {
    setTab(key);
    if (panelRef.current && panelRef.current.scrollIntoView) panelRef.current.scrollIntoView({ block: 'nearest' });
  }, [setTab]);

  const data = profile.data;
  const tabs = useMemo(() => {
    if (!data) return TABS;
    const unsigned = (data.policies || []).filter((p) => !p.ackAt).length;
    const due = (data.reminders || []).filter((r) => !r.done && r.daysLeft !== null && r.daysLeft <= 60).length;
    const badges = {
      record: data.rehireRestoreHours > 0 ? { n: 1, word: 'to review' } : null,
      policies: unsigned ? { n: unsigned, word: 'unsigned' } : null,
      reminders: due ? { n: due, word: 'due soon' } : null
    };
    return TABS.map((t) => (badges[t.key] ? { ...t, badge: badges[t.key].n, badgeWord: badges[t.key].word } : t));
  }, [data]);

  if (profile.status === 'missing') {
    return (
      <Card>
        <EmptyState icon={FiUserX} title="This person isn't on HR's list" action={<Link to=".." className="hrp-btn hrp-btn--ghost hrp-btn--md"><FiArrowLeft aria-hidden="true" /> Back to People</Link>}>
          They may have been deleted permanently, or the link is wrong.
        </EmptyState>
      </Card>
    );
  }
  if (!data && profile.status === 'error') {
    return (
      <Card>
        <EmptyState icon={FiAlertCircle} title="Couldn't load this profile" action={<Btn variant="ghost" onClick={profile.reload}>Try again</Btn>}>
          Something went wrong on our side. Try again in a moment.
        </EmptyState>
      </Card>
    );
  }
  if (!data) return <LoadingProfile />;

  const person = { ...data.person, balances: data.balances };
  const reloadAll = () => {
    profile.reload();
    people.reload();
  };
  const logDone = ({ text }) => {
    setLog(null);
    onToast(text);
    reloadAll();
  };
  const patchEntry = (entry) => profile.patch((d) => ({ ...d, entries: d.entries.map((e) => (e.id === entry.id ? entry : e)) }));
  const removed = () => {
    onToast('Entry removed');
    reloadAll();
  };
  const balancesSaved = (balances) => {
    if (balances) profile.patch((d) => ({ ...d, balances }));
    onToast('Allowances saved');
    reloadAll();
  };
  const patchPerson = (next) => profile.patch((d) => ({ ...d, person: { ...d.person, ...next } }));
  const deleted = (name) => {
    onToast(`${name} deleted permanently`);
    people.reload();
    navigate('..', { replace: true });
  };

  let panel;
  if (tab === 'record') {
    panel = <ProfileRecord data={data} me={me} people={people} today={today} onToast={onToast} onReload={profile.reload} onPatchPerson={patchPerson} onDeleted={deleted} />;
  } else if (tab === 'approvals') {
    panel = <ProfileApprovals data={data} people={people} types={types} onToast={onToast} onReload={profile.reload} />;
  } else if (tab === 'team') {
    panel = <ProfileTeam data={data} />;
  } else if (tab === 'policies') {
    panel = <ProfilePolicies data={data} />;
  } else if (tab === 'checklists') {
    panel = <ProfileChecklists data={data} today={today} onToast={onToast} onReload={profile.reload} onPatch={profile.patch} />;
  } else if (tab === 'reminders') {
    panel = <ProfileReminders data={data} />;
  } else if (tab === 'changelog') {
    panel = <ProfileChangeLog data={data} types={types} />;
  } else {
    panel = (
      <ProfileTimeOff
        data={data}
        types={types}
        holidays={holidays}
        today={today}
        onLog={() => setLog({ entry: null })}
        onEdit={(entry) => setLog({ entry })}
        onRemoved={removed}
        onPatchEntry={patchEntry}
        onBalancesSaved={balancesSaved}
        onTab={goTab}
      />
    );
  }

  return (
    <div className="hrp-page hrp-pf">
      <Header data={data} today={today} />
      <Tabs tabs={tabs} current={tab} onChange={setTab} idBase={idBase} />
      <div
        ref={panelRef}
        role="tabpanel"
        id={`${idBase}-panel`}
        aria-labelledby={`${idBase}-tab-${tab}`}
        className="hrp-pf-panel"
      >
        {panel}
      </div>
      <LogTimeOffDrawer
        open={!!log}
        person={person}
        entry={log ? log.entry : null}
        types={types}
        holidays={holidays}
        yearRange={data.year === (yearRange && yearRange.year) ? yearRange : null}
        today={today}
        context={context}
        onClose={() => setLog(null)}
        onDone={logDone}
      />
    </div>
  );
}

export default PersonProfile;
